import { defer, timer, throwError, type Observable, type ObservableInput } from 'rxjs';
import { retry } from 'rxjs/operators';

const DEFAULT_MAX_RETRIES = 3;
const DEFAULT_INITIAL_DELAY_MS = 1000;

type ApiError = {
  response?: {
    status?: number;
  };
  message?: string;
};

type RetryWithBackoffOptions<T> = {
  apiFactory: () => ObservableInput<T>;
  maxRetries?: number;
  initialDelayMs?: number;
  shouldRetry?: (error: ApiError) => boolean;
};

export const isAuthError = (error: unknown): boolean => {
  const status = (error as ApiError)?.response?.status;
  return status === 401 || status === 403;
};

export function retryWithBackoff<T>({
  apiFactory,
  maxRetries = DEFAULT_MAX_RETRIES,
  initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
  shouldRetry = (error: ApiError) => !isAuthError(error),
}: RetryWithBackoffOptions<T>): Observable<T> {
  return defer(apiFactory).pipe(
    retry({
      count: maxRetries,
      delay: (error: ApiError, retryCount: number) => {
        if (!shouldRetry(error)) {
          return throwError(() => error);
        }
        const delayMs = initialDelayMs * 2 ** (retryCount - 1);
        return timer(delayMs);
      },
    }),
  );
}
